import AgoraRTC, { createClient } from 'agora-rtc-sdk-ng';
import { useEffect, useRef, useState } from 'react';
import { AGORA_APP_ID } from '../../../../config';
import useAppAccessor from '../../../../hooks/useappAccessor';
import useAppDispatch from '../../../../hooks/useAppDispatch';
import { rtmClient } from '../../../agora-chat';
import { changeAudio, changeVideo, setUids, turnOffCamera, turnOffScreenSharing, turnOffScreenSharingToast, turnOnCamera, turnOnScreenSharingToast } from "../../../agora-live/components/streamControls/controls.actions";
import { setHostConnectionStatus, setPresenterNum, setSlowNetwork } from '../../../live-main/livemain.actions';
import VideoRoomCopresenterLayout from '../videoRoomCopresenterLayout';
import { updateRecord } from '../../../../services/record.service';

const APP_ID = AGORA_APP_ID;

let rtc = {
  localAudioTrack: null,
  localVideoTrack: null,
  localScreenTrack: null,
  localScreenAudioTrack: null,
  client: null,
  rtmChannel: null,
  screenSharingEventHandler: null,
};
AgoraRTC.setLogLevel(4);

let agoraCommandQueue = Promise.resolve();

const isAttendee = () => {
  return window.location.href.indexOf('/stream') == -1
}

const isHost = () => {
  return window.location.href.indexOf('/stream') > -1
}

const createAgoraClient = ({
  onVideoTrack,
  onUserUnpublished,
  onUserDisconnected,
}) => {
  const client = createClient({
    mode: 'live',
    codec: 'vp8',
  });

  rtc.client = client;

  let tracks;

  const waitForConnectionState = (connectionState, timeout = 10000) => {
    return new Promise((resolve, reject) => {
      if (client.connectionState === connectionState) {
        resolve();
        return;
      }
      
      const startTime = Date.now();
      const interval = setInterval(() => {
        if (client.connectionState === connectionState) {
          clearInterval(interval);
          resolve();
        } else if (Date.now() - startTime > timeout) {
          clearInterval(interval);
          console.warn(`[waitForConnectionState] Timeout waiting for ${connectionState}`);
          resolve();
        }
      }, 200);
    });
  };

  const connect = async (stream_instance_id, token, stream_vendor_user_id, setUsers, rolePresenter, setPoorInternet, newVideoStream, newAudioStream, dispatch, usePortrait, hostUid = null, getIsScreenSharing = null, isMicOn = true, isCameraOn = true, getScreenSharingUid = null, setScreenSharingUid = null, sendScreenSharingMessage = null, getCopresenterInfo = null) => {
    await waitForConnectionState('DISCONNECTED');

    if (!rolePresenter && !isHost()) {
      await client.setClientRole('audience');
    } else {
      await client.setClientRole('host');
    }  

    const uid = await client.join(
      APP_ID,
      stream_instance_id,
      token,
      stream_vendor_user_id
    );

    if (isHost()) {
      dispatch(setHostConnectionStatus(true));
      
      client.on('connection-state-change', (curState, revState) => {
        if (curState === 'DISCONNECTED' || curState === 'RECONNECTING' || curState === 'FAILED') {
          dispatch(setHostConnectionStatus(false));
        } else if (curState === 'CONNECTED') {
          dispatch(setHostConnectionStatus(true));
        }
      });
    }

    client.on('user-published', (user, mediaType) => {
      if (!isHost() && hostUid && user.uid === hostUid) {
        dispatch(setHostConnectionStatus(true));
      }
      
      client.subscribe(user, mediaType).then(() => {
        if (mediaType === 'video') {
          onVideoTrack(user);
        }

        if (mediaType === 'audio') {
          user.audioTrack?.play();
        }
      });
    });

    client.on('user-unpublished', (user, mediaType) => {
        if (mediaType === 'audio') {
          user.audioTrack?.stop();
        }

        onUserUnpublished(user, mediaType);
    });

    client.on('user-left', (user) => {
      if (!isHost() && hostUid && user.uid === hostUid) {
        dispatch(setHostConnectionStatus(false));
      }
      
      if (setScreenSharingUid && getScreenSharingUid && getScreenSharingUid() === user.uid) {
        setScreenSharingUid(null);
      }
      
      // if (sendScreenSharingMessage && rtc.rtmChannel && uid && user.uid === uid) {
      //   sendScreenSharingMessage(false, uid).catch(err => {
      //     console.warn('[ScreenSharingMessage] Failed to send screen sharing stop on user disconnect:', err);
      //   });
      // }
      
      onUserDisconnected(user);
    });

    client.enableAudioVolumeIndicator();

    client.on('exception', (evt) => {
      if (evt.code === 1001 || evt.code === 3001 || 
          evt.msg === 'FRAMERATE_INPUT_TOO_LOW' || 
          evt.msg === 'FRAMERATE_INPUT_TOO_LOW_RECOVER') {
        console.warn('[AgoraClient] Frame rate warning (code:', evt.code, '):', evt.msg, '- This is normal for static content and will recover automatically');
      } else {
        console.warn('[AgoraClient] Exception:', evt);
      }
    });

    await waitForConnectionState('CONNECTED');

    if (typeof client.createDataStream === 'function') {
      try {
        const dataStream = await client.createDataStream({ ordered: true });
        rtc.dataStream = dataStream;
      } catch (error) {
        console.warn('[ScreenSharingMessage] Failed to create data stream:', error);
        rtc.dataStream = null;
      }
    } else {
      console.warn('[ScreenSharingMessage] createDataStream method not available in this Agora RTC SDK version. Remote screen sharing messaging will be disabled.');
      rtc.dataStream = null;
    }

    client.on('volume-indicator', (speakingUser) => {
      setUsers((previousUsers) => {

        const findScreenSharingUserIndex = (users) => {
          for (let i = 0; i < users.length; i++) {
            if (users[i]?.isScreenSharing) {
              return i;
            }
          }
          return -1;
        };


        const screenSharingIndex = findScreenSharingUserIndex(previousUsers);
        if (screenSharingIndex !== -1) {
          const screenSharingUser = previousUsers[screenSharingIndex];
          const rest = previousUsers.filter((_, i) => i !== screenSharingIndex);
          return [screenSharingUser, ...rest];
        }


        var tmpUser = [];
        let allVolumesZero = true;

        //For each user in the previous users list, check if the user is speaking
        for(let i = 0; i < previousUsers.length; i++) {
          let found = false;
          for(let j = 0; j < speakingUser.length; j++) {

            if (previousUsers[i].uid == speakingUser[j].uid) {
              found = true;

              const volumeLevel = speakingUser[j].level ?? 0;

              if (volumeLevel > 0) {
                allVolumesZero = false;
              }

              tmpUser.push({
                uid: speakingUser[j].uid,
                timestamp: previousUsers[i].timestamp ?? new Date().getTime(),
                isAttendee: isAttendee(),
                audioTrack: previousUsers[i].audioTrack,
                videoTrack: previousUsers[i].videoTrack,
                firstName: previousUsers[i].firstName,
                lastName: previousUsers[i].lastName,
                isCameraOn: previousUsers[i].isCameraOn,
                isMicOn: previousUsers[i].isMicOn,
                isScreenSharing: previousUsers[i].isScreenSharing,
                volume: volumeLevel,
                rotateDegree: previousUsers[i].rotateDegree ?? 0,
              });
            }
          }

          if (!found) {
            tmpUser.push({
              ...previousUsers[i],
              volume: previousUsers[i].volume ?? 0,
            });
          }
        }
        
        if (allVolumesZero) {
          return previousUsers;
        }
        
        tmpUser.sort((a, b) => {
          return (b.volume ?? 0) - (a.volume ?? 0);
        });

        const tmpScreenSharingIndex = tmpUser.findIndex((u) => u?.isScreenSharing);
        if (tmpScreenSharingIndex !== -1) {
          const screenSharingUser = tmpUser[tmpScreenSharingIndex];
          const rest = tmpUser.filter((_, i) => i !== tmpScreenSharingIndex);
          tmpUser = [screenSharingUser, ...rest];
        }

        return tmpUser;
      });
    });

    client.on('network-quality', (stats) => {
      
      if (stats.downlinkNetworkQuality >= 5 || stats.uplinkNetworkQuality >= 5) {
        setPoorInternet(true);
      } else {
        setPoorInternet(false);
      }
    });

    // if (!isAttendee()) {
      // tracks =
      // await AgoraRTC.createMicrophoneAndCameraTracks();

      if (rolePresenter) {
        var micId = '';
        if (newAudioStream) {
          micId = newAudioStream;
        } else {
          const mics = await AgoraRTC.getMicrophones();
          if (mics.length > 0) {
            micId = mics[0].deviceId;
            dispatch(changeAudio(micId));
          }
        }
        rtc.localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
          microphoneId: micId
        });

        var cameraId = '';
        if (newVideoStream) {
          cameraId = newVideoStream;
        } else {
          const devices = await AgoraRTC.getCameras();
          if (devices.length > 0) {
            cameraId = devices[0].deviceId;
            dispatch(changeVideo(cameraId));
          }
        }
        rtc.localVideoTrack = await AgoraRTC.createCameraVideoTrack({
          encoderConfig: usePortrait ? {
            width: 360,
            height: 640,
            frameRate: 30,
            bitrateMax: 1000,
            orientationMode: 'adaptative',
            captureWidth: 1920,
            captureHeight: 1080,
            cropMode: 'center',
            scaleMode: 'CROP_FILL'
          } : "720p_5",
          source: 'camera',
          cameraId: cameraId
        });
        rtc.localVideoTrack.streamId = 'camera_stream';
        rtc.localAudioTrack.setEnabled(isMicOn);
        rtc.localVideoTrack.setEnabled(isCameraOn);
        tracks = [rtc.localAudioTrack, rtc.localVideoTrack];
        await client.publish(tracks);
         
      }


      // await client.publish(tracks);
    // }

    return {
      tracks,
      uid,
    };
  };

  const disconnect = async () => {
    await waitForConnectionState('CONNECTED');
    client.removeAllListeners();
    for (let track of tracks) {
      track.stop();
      track.close();
    }
    await client.unpublish(tracks);
    await client.leave();
  };

  return {
    disconnect,
    connect,
  };
};

export const VideoRoom = () => {
  const [users, setUsers] = useState([]);
  const [poorInternet, setPoorInternet] = useState(false);
  const [uid, setUid] = useState(null);
  const [inited, setInited] = useState(false);
  const [screenSharingUid, setScreenSharingUid] = useState(null);
  const screenSharingUidRef = useRef(null);
  const uidRef = useRef(null);

  useEffect(() => {
    screenSharingUidRef.current = screenSharingUid;
  }, [screenSharingUid]);
  
  useEffect(() => {
    uidRef.current = uid;
  }, [uid]);
  
  const { getLiveMainData, getUserData } = useAppAccessor();
  const { createStreamData, tokenData, attendeeTokenData, joinWithAttendeeData, joinData, attendeeList, rtmTokenData } = getLiveMainData();
  
  const getHostUid = () => {
    if (isHost()) {
      return createStreamData['metadata']?.['attendees']?.[0]?.['stream_vendor_user_id'] ?? null;
    } else if (joinWithAttendeeData?.attendees?.length > 0) {
      return joinWithAttendeeData.attendees[0].stream_vendor_user_id;
    }
    return null;
  };
  
  const dispatch = useAppDispatch()
  const { getControls } = useAppAccessor();
  const { isCameraOn, isMicOn, coPresenterInfo, roleCopresenter, isScreenSharing, cameraOnBeforeScreenShare, newVideoStream, newAudioStream, usePortrait, rotateDegree, isRecord } = getControls();
  const { user } = getUserData();
  const { publicUserData } = user;
  const { streamState } = getLiveMainData();

  //HELPER
  const isScreenShareTrack = (videoTrack) => {
    if (!videoTrack) return false;
    
    if (videoTrack.streamId === 'screen_stream') {
      return true;
    }
    
    if (videoTrack.getMediaStreamTrack) {
      const mediaTrack = videoTrack.getMediaStreamTrack();
      if (mediaTrack) {
        const label = mediaTrack.label?.toLowerCase() || '';
        if (label.includes('screen') || label.includes('desktop')) {
          return true;
        }
      }
    }
    
    return false;
  };

  const reorderUsersWithScreenShareFirst = (users) => {
    const screenSharingIndex = users.findIndex(user =>
      isScreenShareTrack(user.videoTrack)
    );
    
    if (screenSharingIndex > 0) {
      const reordered = [...users];
      const screenSharingUser = reordered.splice(screenSharingIndex, 1)[0];
      reordered.unshift(screenSharingUser);
      return reordered;
    }
    
    return users;
  };

  const reorderUsersByScreenSharing = (users) => {
    return [...users].sort((a, b) => {
      const aIsSharing = a.isScreenSharing ? 1 : 0;
      const bIsSharing = b.isScreenSharing ? 1 : 0;
      return bIsSharing - aIsSharing;
    });
  };
    
  const populateCopresenter = (stateUser) => {
    if (coPresenterInfo && coPresenterInfo.length > 0) {

      for (let i = 0; i < coPresenterInfo.length; i++) {
        if (coPresenterInfo[i].uid == stateUser.uid) {
          return {
              uid: stateUser.uid,
              timestamp: stateUser.timestamp,
              audioTrack: stateUser.audioTrack,
              videoTrack: stateUser.videoTrack,
              firstName: coPresenterInfo[i].firstName,
              lastName: coPresenterInfo[i].lastName,
              isCameraOn: !coPresenterInfo[i].videoOff,
              isMicOn: !coPresenterInfo[i].micOff,
              isScreenSharing: coPresenterInfo[i]?.isScreenSharing,
              rotateDegree: coPresenterInfo[i].rotateDegree ?? 0,
          };
        }
      }

    }
    return stateUser;
  }

  const isPresenter = () => {
    return isHost() || roleCopresenter;
  }

  const getUserNamesFromAttendeeData = (uid) => {
    let firstName = null;
    let lastName = null;

    if (joinWithAttendeeData && joinWithAttendeeData.attendees && joinWithAttendeeData.attendees.length > 0) {
      const attendee = joinWithAttendeeData.attendees.find(
        (a) => a.stream_vendor_user_id === uid
      );
      if (attendee) {
        firstName = attendee.given_name;
        lastName = attendee.family_name;
      }
    }

    if ((!firstName || !lastName) && attendeeList && attendeeList.length > 0) {
      const attendee = attendeeList.find((a) => a.uid === uid);
      if (attendee) {
        firstName = attendee.firstName || firstName;
        lastName = attendee.lastName || lastName;
      }
    }

    return { firstName, lastName };
  };

  //******************************LOCAL STATE UPDATE**************************************
  useEffect(() => {
    if(!inited)
    {
      return;
    }
      
    if (!isPresenter()) {
      return;
    }

    if (isCameraOn) {
      if (rtc.localVideoTrack) {
        rtc.localVideoTrack.setEnabled(true);
      } else {
        console.error('[CameraDebug] videoRoom: Cannot enable camera - rtc.localVideoTrack is null/undefined');
      }
    } else {
      if (rtc.localVideoTrack) {
        rtc.localVideoTrack.setEnabled(false);
      } else {
        console.error('[CameraDebug] videoRoom: Cannot disable camera - rtc.localVideoTrack is null/undefined');
      }
    }

    if (inited) {
      setUsers((previousUsers) => {
      if (previousUsers && previousUsers.find((u) => u.uid === uid)) {
        var tmpUser = [];
        for(let i = 0; i < previousUsers.length; i++) {
          if (previousUsers[i].uid != uid) {
            tmpUser.push(populateCopresenter(previousUsers[i]));
          } else {
            var tempUser = populateCopresenter(previousUsers[i]);
            tempUser.isCameraOn = isCameraOn;
            tmpUser.push(tempUser);
          }
        }
        return tmpUser;
      } else {
        return previousUsers;
      }
    });
    }
  }, [isCameraOn])

  //******************************PRESENTER RTM ATTRIBUTE UPDATE**************************************

  //When user turnoff / on the camera hit here
  useEffect(() => {
    if (inited) {
      setUsers((previousUsers) => {
        var tmpUser = [];
          for(let i = 0; i < previousUsers.length; i++) {
            tmpUser.push(populateCopresenter(previousUsers[i]));
          }
          return reorderUsersByScreenSharing(tmpUser);
      });
    }
  }, [coPresenterInfo])


  //************************************ACTION TRIGGER*************************************
  const switchToScreenStream = async () => {

    var newVideoTrack, newAudioTrack;

    if (rtc.localVideoTrack) {
      await rtc.localVideoTrack.stop();
      await rtc.localVideoTrack.close();
      await rtc.client.unpublish(rtc.localVideoTrack);
      rtc.localVideoTrack = null;
    }
    rtc.client.unpublish(rtc.localVideoTrack);

    setTimeout(async () =>  {
      try {
        const config = {
          audio: true,
          encoderConfig: {
            width: 1280,
            height: 720,
            frameRate: 30,
            bitrate: 3000,
        },
        optimizationMode: "detail",
        source: 'screen',
        };

      var tracks = await AgoraRTC.createScreenVideoTrack(
        config, "auto"
      );
  
      if (Array.isArray(tracks)) {
        newVideoTrack = tracks[0];
        newAudioTrack = tracks[1];
      } else if(tracks) {
        newVideoTrack = tracks;
        newAudioTrack = null;
      }
  
      newVideoTrack.streamId = 'screen_stream';
      newVideoTrack.on("track-ended", async () => {
        // await sendScreenSharingMessage(false, uid);

        if (cameraOnBeforeScreenShare) {
          dispatch(turnOnCamera());
        } else {
          dispatch(turnOffScreenSharing());
        }
        dispatch(turnOffScreenSharing());
      });

      newVideoTrack.on("exception", (evt) => {
        if (evt.code === 1001 || evt.code === 3001 || 
            evt.msg === 'FRAMERATE_INPUT_TOO_LOW' || 
            evt.msg === 'FRAMERATE_INPUT_TOO_LOW_RECOVER') {
        } else {
          console.warn('[ScreenSharing] Track exception:', evt);
        }
      });
  
      rtc.localScreenTrack = newVideoTrack;
      rtc.localScreenAudioTrack = newAudioTrack;

      setUsers((previousUsers) => {
        var tmpUser = [];
        for(let i = 0; i < previousUsers.length; i++) {
          if (previousUsers[i].uid == uid) {
            tmpUser.push({
              uid: previousUsers[i].uid,
              timestamp: new Date().getTime(),
              isAttendee: isAttendee(),
              audioTrack: previousUsers[i].audioTrack,
              videoTrack: newVideoTrack,
              firstName: previousUsers[i].firstName,
              isScreenSharing: previousUsers[i].isScreenSharing,
              lastName: previousUsers[i].lastName,
              isCameraOn: true,
              isMicOn: previousUsers[i].isMicOn,
              rotateDegree: previousUsers[i].rotateDegree ?? 0,
            });
          } else {
            tmpUser.push(previousUsers[i]);
          }
        }
        return reorderUsersWithScreenShareFirst(tmpUser);
      });

      const tracksToPublish = [newVideoTrack];
      if (newAudioTrack) {
        tracksToPublish.push(newAudioTrack);
      }

      if (rtc.localAudioTrack) {
        await rtc.localAudioTrack.setEnabled(true);
        tracksToPublish.push(rtc.localAudioTrack);
      }

      await rtc.client.publish(tracksToPublish);
      
      // await sendScreenSharingMessage(true, uid);
      
      if (isRecord && uid) {
        updateRecord(uid).catch(err => {
          console.warn('[RecordUpdate] Failed to update record with screen sharing uid:', err);
        });
      }
      
      dispatch(turnOnScreenSharingToast());
      } catch (e) {
          if (rtc.localScreenTrack) {
            await rtc.localScreenTrack.stop();
            await rtc.localScreenTrack.close();
            await rtc.client.unpublish(rtc.localScreenTrack);
            rtc.localScreenTrack = null;
          }
          if (rtc.localScreenAudioTrack) {
            await rtc.localScreenAudioTrack.stop();
            await rtc.localScreenAudioTrack.close();
            await rtc.client.unpublish(rtc.localScreenAudioTrack);
            rtc.localScreenAudioTrack = null;
          }
          if (rtc.localVideoTrack) {
            await rtc.localVideoTrack.stop();
            await rtc.localVideoTrack.close();
            await rtc.client.unpublish(rtc.localVideoTrack);
            rtc.localVideoTrack = null;
          }
          
          // Restore camera stream when screen sharing fails
          try {
            var restoreVideoTrack = await AgoraRTC.createCameraVideoTrack({
              cameraId: newVideoStream,
              encoderConfig: usePortrait ? {
                width: 360,
                height: 640,
                frameRate: 30,
                bitrateMax: 1000,
                orientationMode: 'adaptative',
                captureWidth: 1920,
                captureHeight: 1080,
                cropMode: 'center',
                scaleMode: 'CROP_FILL'
              } : "720p_5",
              optimizationMode: "detail",
              source: 'camera',
            });
            
            rtc.localVideoTrack = restoreVideoTrack;
            
            // Republish camera and audio tracks
            const restoreTracks = [restoreVideoTrack];
            if (rtc.localAudioTrack) {
              rtc.localAudioTrack.setEnabled(isMicOn);
              restoreTracks.push(rtc.localAudioTrack);
            }
            await rtc.client.publish(restoreTracks);
            
            // Update users state
            setUsers((previousUsers) => {
              var tmpUser = [];
              for(let i = 0; i < previousUsers.length; i++) {
                if (previousUsers[i].uid == uid) {
                  tmpUser.push({
                    uid: previousUsers[i].uid,
                    timestamp: new Date().getTime(),
                    isAttendee: isAttendee(),
                    audioTrack: previousUsers[i].audioTrack,
                    videoTrack: restoreVideoTrack,
                    firstName: previousUsers[i].firstName,
                    lastName: previousUsers[i].lastName,
                    isScreenSharing: previousUsers[i].isScreenSharing,
                    isCameraOn: cameraOnBeforeScreenShare,
                    isMicOn: previousUsers[i].isMicOn,
                    rotateDegree: previousUsers[i].rotateDegree ?? 0,
                  });
                } else {
                  tmpUser.push(previousUsers[i]);
                }
              }
              return tmpUser;
            });
            
            restoreVideoTrack.setEnabled(cameraOnBeforeScreenShare);
          } catch (restoreError) {
            // console.log("Error restoring camera after screen share failure", restoreError);
          }
          
          if (cameraOnBeforeScreenShare) {
            dispatch(turnOnCamera());
          } else {
            dispatch(turnOffScreenSharing());
          }
          dispatch(turnOffScreenSharing());
      }
    }, 1000);
  }

  const switchToCameraStream = async () => {
    try {
      var newVideoTrack = await AgoraRTC.createCameraVideoTrack({
        cameraId: newVideoStream, // Use the newVideoStream if provided
        encoderConfig: usePortrait ? {
          width: 360,
          height: 640,
          frameRate: 30,
          bitrateMax: 1000,
          orientationMode: 'adaptative',
          captureWidth: 1920,
          captureHeight: 1080,
          cropMode: 'center',
          scaleMode: 'CROP_FILL'
        } : "720p_5",
        optimizationMode: "detail",
        source: 'camera',
      });

      newVideoTrack.setEnabled(true);

      if (cameraOnBeforeScreenShare) {
        dispatch(turnOnCamera());
      } else {
        dispatch(turnOffCamera());
      }

      if (rtc.localScreenTrack) {
        await rtc.localScreenTrack.stop();
        await rtc.localScreenTrack.close();
        await rtc.client.unpublish(rtc.localScreenTrack);
        rtc.localScreenTrack = null;
      }
      rtc.client.unpublish(rtc.localVideoTrack);

      if (rtc.localScreenAudioTrack) {
        await rtc.localScreenAudioTrack.stop();
        await rtc.localScreenAudioTrack.close();
        await rtc.client.unpublish(rtc.localScreenAudioTrack);
        rtc.localScreenAudioTrack = null;
      }

      if (rtc.localVideoTrack) {
        await rtc.client.unpublish(rtc.localVideoTrack);
      }
      
      rtc.localVideoTrack = newVideoTrack;
      
      // Recreate and republish audio track similar to switchToNewMicStream
      // This ensures attendees maintain audio connection after screen share
      if (rtc.localAudioTrack) {
        try {
          // Unpublish the old audio track
          await rtc.client.unpublish(rtc.localAudioTrack);
          
          // Get the current microphone device ID
          var micDeviceId = newAudioStream;
          if (!micDeviceId) {
            const mics = await AgoraRTC.getMicrophones();
            if (mics.length > 0) {
              micDeviceId = mics[0].deviceId;
            }
          }
          
          // Stop and close the old audio track
          await rtc.localAudioTrack.stop();
          await rtc.localAudioTrack.close();
          
          // Create a new audio track with the same device
          var newAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
            microphoneId: micDeviceId || undefined
          });
          
          // Set enabled state based on isMicOn
          newAudioTrack.setEnabled(isMicOn);
          
          // Replace the old track with the new one
          rtc.localAudioTrack = newAudioTrack;
          
          // Publish both video and audio tracks
          await rtc.client.publish([newVideoTrack, newAudioTrack]);
          
          // Update users state with new audio track
          setUsers((previousUsers) => {
            var tmpUser = [];
            for(let i = 0; i < previousUsers.length; i++) {
              if (previousUsers[i].uid == uid) {
                tmpUser.push({
                  uid: previousUsers[i].uid,
                  timestamp: new Date().getTime(),
                  isAttendee: isAttendee(),
                  audioTrack: newAudioTrack,
                  videoTrack: newVideoTrack,
                  firstName: previousUsers[i].firstName,
                  lastName: previousUsers[i].lastName,
                  isCameraOn: true,
                  isScreenSharing: previousUsers[i].isScreenSharing,
                  isMicOn: previousUsers[i].isMicOn,
                  rotateDegree: previousUsers[i].rotateDegree ?? 0,
                });
              } else {
                tmpUser.push(previousUsers[i]);
              }
            }
            return tmpUser;
          });
        } catch (audioError) {
          // If audio recreation fails, try to republish existing track
          console.error('Error recreating audio track:', audioError);
          if (rtc.localAudioTrack) {
            await rtc.client.publish([newVideoTrack, rtc.localAudioTrack]);
            rtc.localAudioTrack.setEnabled(isMicOn);
          } else {
            await rtc.client.publish([newVideoTrack]);
          }
          
          setUsers((previousUsers) => {
            var tmpUser = [];
            for(let i = 0; i < previousUsers.length; i++) {
              if (previousUsers[i].uid == uid) {
                tmpUser.push({
                  uid: previousUsers[i].uid,
                  timestamp: new Date().getTime(),
                  isAttendee: isAttendee(),
                  audioTrack: rtc.localAudioTrack,
                  videoTrack: newVideoTrack,
                  firstName: previousUsers[i].firstName,
                  lastName: previousUsers[i].lastName,
                  isScreenSharing: previousUsers[i].isScreenSharing,
                  isCameraOn: true,
                  isMicOn: previousUsers[i].isMicOn,
                  rotateDegree: previousUsers[i].rotateDegree ?? 0,
                });
              } else {
                tmpUser.push(previousUsers[i]);
              }
            }
            return tmpUser;
          });
        }
      } else {
        // No audio track exists, just publish video
        await rtc.client.publish([newVideoTrack]);
        
        setUsers((previousUsers) => {
          var tmpUser = [];
          for(let i = 0; i < previousUsers.length; i++) {
            if (previousUsers[i].uid == uid) {
              tmpUser.push({
                uid: previousUsers[i].uid,
                timestamp: new Date().getTime(),
                isAttendee: isAttendee(),
                audioTrack: null,
                videoTrack: newVideoTrack,
                firstName: previousUsers[i].firstName,
                lastName: previousUsers[i].lastName,
                isScreenSharing: previousUsers[i].isScreenSharing,
                isCameraOn: true,
                isMicOn: previousUsers[i].isMicOn,
                rotateDegree: previousUsers[i].rotateDegree ?? 0,
              });
            } else {
              tmpUser.push(previousUsers[i]);
            }
          }
          return tmpUser;
        });
      }
      
      newVideoTrack.setEnabled(cameraOnBeforeScreenShare);
      
      // await sendScreenSharingMessage(false, uid);
      
      if (isRecord) {
        updateRecord(null).catch(err => {
          console.warn('[RecordUpdate] Failed to update record with null uid:', err);
        });
      }
      
      dispatch(turnOffScreenSharing());
      dispatch(turnOffScreenSharingToast());
    } catch (e) {
     // console.log("Error in camera sharing");
     // console.log(e);
      dispatch(turnOffScreenSharing());
      dispatch(turnOffScreenSharingToast());
      if (cameraOnBeforeScreenShare) {
        dispatch(turnOnCamera());
      } else {
        dispatch(turnOffScreenSharing());
      }
    }
  }
  
  const togglePortraitStream = async () => {
    try {
      // 检查当前用户角色是否为audience，如果是则先切换为host
      const clientRole = rtc.client.role;
      if (clientRole === 'audience' && isHost()) {
        // 切换为host角色
        await rtc.client.setClientRole('host');
       // console.log('User role switched from audience to host');
      }
      
      var newVideoTrack = await AgoraRTC.createCameraVideoTrack({
        cameraId: newVideoStream, // Use the newVideoStream if provided
        encoderConfig: usePortrait ? {
          width: 360,
          height: 640,
          frameRate: 30,
          bitrateMax: 1000,
          orientationMode: 'adaptative',
          captureWidth: 1920,
          captureHeight: 1080,
          cropMode: 'center',
          scaleMode: 'CROP_FILL'
        } : "720p_5",
        optimizationMode: "detail",
        source: 'camera',
      });

      var newVideoTrack1 = newVideoTrack;
      // try {
      //   newVideoTrack1 = await rotateVideo(newVideoTrack, 90);
      // } catch (e) {
      //  // console.log(e);
      // }

      newVideoTrack1.setEnabled(true);

      if (rtc.localScreenTrack) {
        await rtc.localScreenTrack.stop();
        await rtc.localScreenTrack.close();
        await rtc.client.unpublish(rtc.localScreenTrack);
        rtc.localScreenTrack = null;
      }
      rtc.client.unpublish(rtc.localVideoTrack);

      if (rtc.localScreenAudioTrack) {
        await rtc.localScreenAudioTrack.stop();
        await rtc.localScreenAudioTrack.close();
        await rtc.client.unpublish(rtc.localScreenAudioTrack);
        rtc.localScreenAudioTrack = null;
      }
      rtc.client.unpublish(rtc.localScreenAudioTrack);

      if (rtc.localVideoTrack) {
        await rtc.client.unpublish(rtc.localVideoTrack);
      }
      rtc.client.unpublish(rtc.localScreenAudioTrack);
      rtc.localVideoTrack = newVideoTrack1;
      setUsers((previousUsers) => {
        var tmpUser = [];
        for(let i = 0; i < previousUsers.length; i++) {
          if (previousUsers[i].uid == uid) {
            tmpUser.push({
              uid: previousUsers[i].uid,
              timestamp: new Date().getTime(),
              isAttendee: isAttendee(),
              audioTrack: previousUsers[i].audioTrack,
              videoTrack: newVideoTrack1,
              firstName: previousUsers[i].firstName,
              lastName: previousUsers[i].lastName,
              isCameraOn: true,
              isScreenSharing: previousUsers[i].isScreenSharing,
              isMicOn: previousUsers[i].isMicOn,
              rotateDegree: previousUsers[i].rotateDegree ?? 0,
            });
          } else {
            tmpUser.push(previousUsers[i]);
          }
        }
        return tmpUser;
      });
      await rtc.client.publish([newVideoTrack1]);
    } catch (e) {
      // togglePortraitStream();
     // console.log("Error in togglePortraitStream");
     // console.log(e);
    }
  }

  const switchToNewCameraStream = async (deviceId) => {
    try {
      if (rtc.localVideoTrack) {
        await rtc.localVideoTrack.stop();
        await rtc.localVideoTrack.close();
        await rtc.client.unpublish(rtc.localVideoTrack);
        rtc.localVideoTrack = null;
      }
      // It seems there's a redundant unpublish here, it might be rtc.client.unpublish([rtc.localVideoTrack]);
      // However, the original code had "rtc.client.unpublish(rtc.localVideoTrack);" which might be a typo
      // or specific to an older SDK version. For now, I will keep it as is to minimize unintended changes
      // beyond the primary request. If issues arise, this line should be reviewed.
      // await rtc.client.unpublish(rtc.localVideoTrack); // This line seems redundant or potentially problematic if localVideoTrack is already null.
      await rtc.client.unpublish(rtc.localVideoTrack);
      var newVideoTrack = await AgoraRTC.createCameraVideoTrack({
        cameraId: deviceId, // Use the provided deviceId
        encoderConfig: usePortrait ? {
          width: 360,
            height: 640,
          frameRate: 30,
          bitrateMax: 1000,
          orientationMode: 'adaptative',
          captureWidth: 1920,
          captureHeight: 1080,
          cropMode: 'center',
          scaleMode: 'CROP_FILL'
        } : "720p_5",
        optimizationMode: "detail",
        source: 'camera',
      });

      newVideoTrack.setEnabled(true);

      // if (rtc.localScreenTrack) {
      //   await rtc.localScreenTrack.stop();
      //   await rtc.localScreenTrack.close();
      //   await rtc.client.unpublish(rtc.localScreenTrack);
      //   rtc.localScreenTrack = null;
      // }
      // if (rtc.localScreenAudioTrack) {
      //   await rtc.localScreenAudioTrack.stop();
      //   await rtc.localScreenAudioTrack.close();
      //   await rtc.client.unpublish(rtc.localScreenAudioTrack);
      //   rtc.localScreenAudioTrack = null;
      // }
  
      rtc.localVideoTrack = newVideoTrack;
      setUsers((previousUsers) => {
        var tmpUser = [];
        for(let i = 0; i < previousUsers.length; i++) {
          if (previousUsers[i].uid == uid) {
            tmpUser.push({
              uid: previousUsers[i].uid,
              timestamp: new Date().getTime(),
              isAttendee: isAttendee(),
              audioTrack: previousUsers[i].audioTrack,
              videoTrack: newVideoTrack,
              firstName: previousUsers[i].firstName,
              lastName: previousUsers[i].lastName,
              isScreenSharing: previousUsers[i].isScreenSharing,
              isCameraOn: true,
              isMicOn: previousUsers[i].isMicOn,
              rotateDegree: previousUsers[i].rotateDegree ?? 0,
            });
          } else {
            tmpUser.push(previousUsers[i]);
          }
        }
        return tmpUser;
      });
      // var tracks = [rtc.localVideoTrack];
      // if (cameraOnBeforeScreenShare) {
        // rtc.localVideoTrack.setEnabled(true);
      // }
      await rtc.client.publish([newVideoTrack]);
      // newVideoTrack.setEnabled(cameraOnBeforeScreenShare);
      // dispatch(turnOffScreenSharing());
      // dispatch(turnOffScreenSharingToast());
    } catch (e) {
      //// console.log("Error in camera sharing");
      //// console.log(e);
      // dispatch(turnOffScreenSharing());
      // dispatch(turnOffScreenSharingToast());
      // if (cameraOnBeforeScreenShare) {
      //   dispatch(turnOnCamera());
      // } else {
      //   dispatch(turnOffScreenSharing());
      // }
    }
  }

  const switchToNewMicStream = async (deviceId) => {
    try {
      rtc.client.unpublish(rtc.localAudioTrack);
      if (rtc.localAudioTrack) {
        await rtc.localAudioTrack.stop();
        await rtc.localAudioTrack.close();
        await rtc.client.unpublish(rtc.localAudioTrack);
        rtc.localAudioTrack = null;
      }

      var newAudioTrack = await AgoraRTC.createMicrophoneAudioTrack({
        microphoneId: deviceId,
      });

      newAudioTrack.setEnabled(isMicOn);
  
      rtc.localAudioTrack = newAudioTrack;
      setUsers((previousUsers) => {
        var tmpUser = [];
        for(let i = 0; i < previousUsers.length; i++) {
          if (previousUsers[i].uid == uid) {
            tmpUser.push({
              uid: previousUsers[i].uid,
              timestamp: new Date().getTime(),
              isAttendee: isAttendee(),
              audioTrack: newAudioTrack,
              videoTrack: previousUsers[i].videoTrack,
              firstName: previousUsers[i].firstName,
              isScreenSharing: previousUsers[i].isScreenSharing,
              lastName: previousUsers[i].lastName,
              isCameraOn: previousUsers[i].isCameraOn,
              isMicOn: true,
              rotateDegree: previousUsers[i].rotateDegree ?? 0,
            });
          } else {
            tmpUser.push(previousUsers[i]);
          }
        }
        return tmpUser;
      });
      await rtc.client.publish([newAudioTrack]);
    } catch (e) {
     // console.log("Error in mic sharing");
     // console.log(e);
      // dispatch(turnOffScreenSharing());
      // dispatch(turnOffScreenSharingToast());
      // if (cameraOnBeforeScreenShare) {
      //   dispatch(turnOnCamera());
      // } else {
      //   dispatch(turnOffScreenSharing());
      // }
    }
  }

  useEffect(() => {
    // setTimeout(() => {
      if (newVideoStream && isPresenter()) {
        switchToNewCameraStream(newVideoStream);
      }
    // },1000);
  }, [newVideoStream])

  useEffect(() => {
     if (!isScreenSharing) {
      //// console.log(usePortrait);
      togglePortraitStream();
     }
  }, [usePortrait])

  useEffect(() => {
    if(newAudioStream) {
      switchToNewMicStream(newAudioStream);
    }
  }, [newAudioStream])

  useEffect(() => {
    if (streamState === 'ended') {
      rtc.client.removeAllListeners();
      if(rtc.localVideoTrack) {
        rtc.localVideoTrack.stop();
        rtc.localVideoTrack.setEnabled(false);
      }
      if(rtc.localScreenAudioTrack) {
        rtc.localAudioTrack.stop();
        rtc.localAudioTrack.setEnabled(false);
      }
      if(rtc.localScreenTrack) {
        rtc.localScreenTrack.stop();
      }
      rtc.client.leave();
    }
  }, [streamState]);

  useEffect(() => {
    dispatch(setSlowNetwork(poorInternet));
  }, [poorInternet])

  useEffect(() => {
    if (!isPresenter()) {
      return;
    }

    if (inited) {
      setUsers((previousUsers) => {
      if (previousUsers && previousUsers.find((u) => u.uid === uid)) {
        var tmpUser = [];
        for(let i = 0; i < previousUsers.length; i++) {
          if (previousUsers[i].uid != uid) {
            tmpUser.push(populateCopresenter(previousUsers[i]));
          } else {
            var tempUser = populateCopresenter(previousUsers[i]);
            tempUser.rotateDegree = rotateDegree;
            tmpUser.push(tempUser);
          }
        }
        return tmpUser;
      } else {
        return previousUsers;
      }
    });
    }
  }, [rotateDegree])

  useEffect(() => {
    if (inited && isPresenter()) {
      if (isScreenSharing) {
        switchToScreenStream();
      } else {
        switchToCameraStream();
      }
    }
  }, [isScreenSharing])


  useEffect(() => {
    dispatch(setPresenterNum(users.length));
  }, [users])

  useEffect(() => {
    if (!isPresenter()) {
      return;
    }
    if (isMicOn) {
      if (rtc.localAudioTrack) {
        rtc.localAudioTrack.setEnabled(true);
      }
    } else {
      if (rtc.localAudioTrack) {
        rtc.localAudioTrack.setEnabled(false);
      }
    }

    if (inited) {
      setUsers((previousUsers) => {
      if (previousUsers && previousUsers.find((u) => u.uid === uid)) {
        var tmpUser = [];
        for(let i = 0; i < previousUsers.length; i++) {
          if (previousUsers[i].uid != uid) {
            tmpUser.push(populateCopresenter(previousUsers[i]));
          } else {
            var tempUser = populateCopresenter(previousUsers[i]);
            tempUser.isMicOn = isMicOn;
            tmpUser.push(tempUser);
          }
        }
        return tmpUser;
      }
    });
    }
  }, [isMicOn])


  //*************************************************************************

  const onVideoTrack = (user) => {
    setUsers((previousUsers) => {
    if (previousUsers.find((u) => u.uid === user.uid)) {
      var tmpUser = [];
      for(let i = 0; i < previousUsers.length; i++) {
        if (previousUsers[i].uid != user.uid) {
          tmpUser.push(previousUsers[i]);
        } else {
          tmpUser.push({
            uid: user.uid,
            timestamp: previousUsers[i].timestamp ?? new Date().getTime(),
            isAttendee: previousUsers[i].isAttendee,
            audioTrack: user.audioTrack,
            videoTrack: user.videoTrack,
            isScreenSharing: previousUsers[i].isScreenSharing,
            firstName: previousUsers[i].firstName,
            lastName: previousUsers[i].lastName,
            isCameraOn: true,
            isMicOn: isMicOn,
            rotateDegree: previousUsers[i].rotateDegree ?? 0,
          });
        }
      }
      return reorderUsersWithScreenShareFirst(tmpUser);
    } else {
      const { firstName, lastName } = getUserNamesFromAttendeeData(user.uid);
      const newUsers = [
        ...previousUsers,
        {
          uid: user.uid,
          isAttendee: isAttendee(),
          timestamp: new Date().getTime(),
          audioTrack: user.audioTrack,
          videoTrack: user.videoTrack,
          firstName: firstName,
          lastName: lastName,
          isCameraOn: true,
          isMicOn: isMicOn,
          rotateDegree: rotateDegree,
        },
      ];
      return reorderUsersWithScreenShareFirst(newUsers);
    }
  });
  }

  const onUserUnpublished = (user, type) => {
    setUsers((previousUsers) => {
    if (previousUsers.find((u) => u.uid === user.uid)) {
      var tmpUser = [];
      for(let i = 0; i < previousUsers.length; i++) {
        if (previousUsers[i].uid != user.uid) {
          tmpUser.push(previousUsers[i]);
        } else {
          tmpUser.push({
            uid: user.uid,
            timestamp: type == 'audio' ? previousUsers[i].timestamp : new Date().getTime(),
            isAttendee: isAttendee(),
            audioTrack: user.audioTrack,
            videoTrack: previousUsers[i].videoTrack,
            firstName: previousUsers[i].firstName,
            lastName: previousUsers[i].lastName,
            isScreenSharing: previousUsers[i].isScreenSharing,
            isCameraOn: type == 'video' ? false : previousUsers[i].isCameraOn,
            isMicOn: type == 'audio' ? false : previousUsers[i].isMicOn,
            rotateDegree: previousUsers[i].rotateDegree ?? 0,
          });
        }
      }
      return tmpUser;
    } else {
      return previousUsers;
    }
  });
  }

  const onUserDisconnected = (user) => {
    setUsers((previousUsers) =>
      previousUsers.filter((u) => u.uid !== user.uid)
    );
  };

  const sendScreenSharingMessage = async (isSharing, currentUid) => {
    const uidToUse = currentUid || uid;
    if (!rtc.rtmChannel || !uidToUse) {
      console.warn('[ScreenSharingMessage] Cannot send RTM message - channel:', rtc.rtmChannel, 'uid:', uidToUse);
      return;
    }

    try {
      const connectionState = rtmClient.connectionState;
      if (connectionState !== 'CONNECTED') {
        console.warn('[ScreenSharingMessage] RTM client not connected, current state:', connectionState, '- skipping message send');
        return;
      }

      const message = JSON.stringify({
        actionType: 'screen-sharing',
        uid: uidToUse,
        isSharing: isSharing
      });
      await rtc.rtmChannel.sendMessage({
        text: message,
        messageType: "TEXT",
      });
    } catch (error) {
      console.warn('[ScreenSharingMessage] Failed to send screen sharing message via RTM:', error, 'connectionState:', rtmClient.connectionState);
    }
  };

  const setupRTMChannel = async (rtmTokenData, stream_instance_id, setScreenSharingUid, getIsScreenSharing = null) => {
    if (!rtmTokenData?.user_id || !rtmTokenData?.token || !stream_instance_id) {
      console.warn('[ScreenSharingMessage] RTM token or stream_instance_id not available');
      return null;
    }

    try {
      let channel = window.agoraRTMChannel;
        
      if (!channel) {
        // console.log('[ScreenSharingMessage] window.agoraRTMChannel not available yet, waiting for chat component to set it up...');
        for (let i = 0; i < 10; i++) {
          await new Promise(resolve => setTimeout(resolve, 200));
          channel = window.agoraRTMChannel;
          if (channel) {
            break;
          }
        }
      }

      return channel;
    } catch (error) {
      console.error('[ScreenSharingMessage] Failed to setup RTM channel:', error);
      return null;
    }
  };

  const init = async () => {



    const { connect, disconnect } = createAgoraClient({
      onVideoTrack,
      onUserUnpublished,
      onUserDisconnected,
    });

    const setup = async () => {
      const hostUid = getHostUid();
      const getIsScreenSharing = () => {
        const currentValue = isScreenSharing;
        return currentValue;
      };
      const getScreenSharingUid = () => {
        const currentUid = screenSharingUidRef.current;
        // console.log('[VolumeIndicator] getScreenSharingUid called, returning:', currentUid);
        return currentUid;
      };
      const getCopresenterInfo = () => {
        return coPresenterInfo;
      };

      let stream_instance_id;
      if (isHost()) {
        stream_instance_id = createStreamData.stream_instance_id;
      } else {
        stream_instance_id = joinData.stream_instance_id;
      }

      if (rtmTokenData && stream_instance_id) {
        await setupRTMChannel(rtmTokenData, stream_instance_id, setScreenSharingUid, getIsScreenSharing);
      }
      
      if (isHost()) {
        const stream_vendor_user_id = createStreamData['metadata']['attendees'][0]['stream_vendor_user_id'] ?? 0;

        const { tracks, uid } = await connect(stream_instance_id, tokenData.token, stream_vendor_user_id, setUsers, true, setPoorInternet, newVideoStream, newAudioStream, dispatch, usePortrait, hostUid, getIsScreenSharing, isMicOn, isCameraOn, getScreenSharingUid, setScreenSharingUid, sendScreenSharingMessage, getCopresenterInfo);

        setUid(uid);
        dispatch(setUids(uid));

        tracks[1].setEnabled(isCameraOn);
        tracks[0].setEnabled(isMicOn);

        setUsers((previousUsers) => [
          ...previousUsers,
          {
            uid,
            audioTrack: tracks[0],
            videoTrack: tracks[1],
            firstName: publicUserData?.family_name,
            lastName: publicUserData?.given_name,
            isCameraOn: isCameraOn,
            isMicOn: isMicOn,
            rotateDegree: rotateDegree,
          },
        ]);
      } else if(isPresenter()) {

        const stream_vendor_user_id = joinData.stream_vendor_user_id;

        const { tracks, uid } = await connect(stream_instance_id, attendeeTokenData.token, stream_vendor_user_id, setUsers, true,setPoorInternet, newVideoStream, newAudioStream, dispatch, usePortrait, hostUid, getIsScreenSharing, isMicOn, isCameraOn, getScreenSharingUid, setScreenSharingUid, sendScreenSharingMessage, getCopresenterInfo);

        setUid(uid);
        tracks[1].setEnabled(isCameraOn);
        tracks[0].setEnabled(isMicOn);
        dispatch(setUids(uid));

        setUsers((previousUsers) => [
          ...previousUsers,
          {
            uid,
            audioTrack: tracks[0],
            videoTrack: tracks[1],
            firstName: publicUserData?.family_name,
            lastName: publicUserData?.given_name,
            isCameraOn: isCameraOn,
            isMicOn: isMicOn,
            rotateDegree: rotateDegree,
          },
        ]);
      } else {
        const stream_vendor_user_id = joinData.stream_vendor_user_id;

        const { tracks, uid } = await connect(stream_instance_id, attendeeTokenData.token, stream_vendor_user_id, setUsers, false,setPoorInternet, newVideoStream, newAudioStream, dispatch, usePortrait, hostUid, getIsScreenSharing, isMicOn, isCameraOn, getScreenSharingUid, setScreenSharingUid, sendScreenSharingMessage, getCopresenterInfo);

        setUid(uid);
        dispatch(setUids(uid));
        
      }
      setTimeout(() => {
        if (joinWithAttendeeData && joinWithAttendeeData.attendees && joinWithAttendeeData.attendees.length > 0) {
          setUsers((previousUsers) => previousUsers.length == 0 ? [
            {
              uid: joinWithAttendeeData.attendees[0].stream_vendor_user_id,
              timestamp: new Date().getTime(),
              firstName: joinWithAttendeeData.attendees[0].given_name,
              lastName: joinWithAttendeeData.attendees[0].family_name,
              isCameraOn: false,
              isMicOn: false,
              rotateDegree: 0,
            },
          ] : [...previousUsers]);
        }
      });
      setInited(true);
    };

    const cleanup = async () => {
      await disconnect();
      if (rtc.rtmChannel && rtc.rtmChannel === window.agoraRTMChannel) {
      } else if (rtc.rtmChannel) {
        try {
          await rtc.rtmChannel.leave();
        } catch (error) {
          console.warn('[ScreenSharingMessage] Error leaving RTM channel:', error);
        }
      }
      rtc.rtmChannel = null;
      setUid(null);
      setUsers([]);
      setInited(false);
    };

    agoraCommandQueue = agoraCommandQueue.then(setup);

    return () => {
      agoraCommandQueue = agoraCommandQueue.then(cleanup);
    };
  };

  useEffect(() => {
    const handleScreenSharingMessage = (event) => {
      const { uid, isSharing } = event.detail;
      if (setScreenSharingUid) {
        if (isSharing) {
          setScreenSharingUid(uid);
        } else {
          setScreenSharingUid(null);
        }
      }
    };

    window.addEventListener('screen-sharing-status', handleScreenSharingMessage);
    rtc.screenSharingEventHandler = handleScreenSharingMessage;

    return () => {
      if (rtc.screenSharingEventHandler) {
        window.removeEventListener('screen-sharing-status', rtc.screenSharingEventHandler);
        rtc.screenSharingEventHandler = null;
      }
    };
  }, [setScreenSharingUid]);

  useEffect(() => {
    init();
  }, []);

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          width: '100%'
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(1, 400px)',
            width: '100%'
          }}
        >
          <VideoRoomCopresenterLayout users={users}></VideoRoomCopresenterLayout>
          {/* {users.map((user) => (
            <VideoPlayer key={user.uid} user={user} />
          ))} */}
        </div>
      </div>
    </>
  );
};